import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionHeading from "@/components/SectionHeading";
import { APPROACH } from "@/data/site";

// Signature interactive 4-step approach. Horizontal on desktop, vertical on mobile.
export default function Approach() {
  const [active, setActive] = useState(0);

  return (
    <section className="section-pad bg-navy-deep" data-testid="approach-section">
      <div className="pointer-events-none absolute" />
      <div className="container-sky">
        <SectionHeading
          light
          align="center"
          eyebrow="Our Approach"
          title="Assess. Advise. Implement. Improve."
          description="A clear four-stage philosophy that shapes everything we do — turning risk into readiness and compliance into lasting safety performance."
          className="mb-16"
        />

        {/* Desktop */}
        <div className="hidden lg:block">
          <div className="relative mb-10">
            <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-white/10" />
            <motion.div
              className="absolute left-0 top-1/2 h-0.5 -translate-y-1/2 bg-gradient-to-r from-brand to-brand-accent"
              initial={{ width: "0%" }}
              animate={{ width: `${((active + 1) / APPROACH.length) * 100}%` }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            />
            <div className="relative grid grid-cols-4">
              {APPROACH.map((step, i) => (
                <button
                  key={step.step}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => setActive(i)}
                  data-testid={`approach-step-${i}`}
                  className="flex flex-col items-start"
                >
                  <span
                    className={`flex h-14 w-14 items-center justify-center rounded-full border-2 font-mono-tech text-sm font-bold transition-all duration-300 ${
                      i <= active
                        ? "border-brand bg-brand text-white"
                        : "border-white/20 bg-navy-deep text-slate-400"
                    }`}
                  >
                    {step.step}
                  </span>
                  <span
                    className={`mt-5 font-heading text-2xl font-bold transition-colors ${
                      i === active ? "text-white" : "text-slate-500"
                    }`}
                  >
                    {step.title}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4 }}
              className="max-w-2xl"
            >
              <p className="text-lg leading-relaxed text-slate-300">{APPROACH[active].description}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mobile vertical */}
        <div className="space-y-5 lg:hidden">
          {APPROACH.map((step) => (
            <div key={step.step} className="relative rounded-2xl border border-white/10 bg-navy-card/50 p-6">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand font-mono-tech text-sm font-bold text-white">
                  {step.step}
                </span>
                <h3 className="font-heading text-xl font-bold text-white">{step.title}</h3>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
